import { Select } from "./ui/select.tsx";
import { notify } from "./feedback.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { useRefresh } from "./components.tsx";
import { minutes, timeZones, usd } from "./format.ts";
import { PRODUCT_SETTINGS_DESIGN as D, SETTINGS_LABELS as L } from "./labels.ts";
import { Button, ErrorText, Field, Modal, Pill, Section, Surface, Switch, ViewSwitch } from "./ui.tsx";
import { CapacityMeter } from "./ui/measure.tsx";

// A product's settings: name, time zone, capacity, and deleting it
// (Settings › Product, plan M5 step 5d). The first implementation's daily
// triage hour and daily email were retired with their jobs (design/plan.md §2).

/** Nobody knows their week to the minute, and plans estimate in rough minutes too: a level is all this can mean. */
const WEEKLY_LEVELS = [120, 240, 480, 960];
/** The agent's own time a week (vision §4.3, 2026-11-03), estimated from runs: levels too. */
const AGENT_LEVELS = [60, 120, 240, 480];

/** Rebuild on a product change so unsaved values cannot cross products. */
export function ProductSettings({ product }: { product: C.ProductDetail }) {
  const productId = product.id;
  const refresh = useRefresh(productId);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...product.settings, name: product.name });
  const [deleting, setDeleting] = useState(false);
  const week = useQuery(q.week(productId)).data;
  const capacity = week?.capacity;
  const save = useMutation({ mutationFn: () => api.patch(`/api/products/${productId}`, form), onSuccess: async () => { notify.success(L.saved); await refresh(); } });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/products/${productId}`),
    onSuccess: async () => { await client.invalidateQueries(); navigate({ to: "/" }); },
  });
  const changed = JSON.stringify(form) !== JSON.stringify({ ...product.settings, name: product.name });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => { setForm({ ...form, [key]: value }); save.reset(); };
  const submit = (event: FormEvent) => { event.preventDefault(); save.mutate(); };
  return <>
    <form onSubmit={submit} className="z-flow">
      <div className="z-card-grid">
      <Surface className="z-setting-group"><Section title={L.product} note={D.productNote}>
        <Field label={L.name}><input className="z-input" aria-label={L.name} value={form.name} onChange={(event) => set("name", event.target.value)} required /></Field>
        <Field label={L.timezone} help={L.timezoneHelp}>
          <Select variant="form" label={L.timezone} value={form.timezone} onValueChange={(nextValue) => set("timezone", nextValue)} options={timeZones().map((zone) => ({ value: zone, label: zone }))} />
        </Field>
      </Section></Surface>
      <Surface className="z-setting-group"><Section title={L.capacity} note={L.capacityNote}>
        <ViewSwitch label={L.capacity} value={String(form.weekly_minutes)} onValueChange={(next) => set("weekly_minutes", Number(next))} options={WEEKLY_LEVELS.map((level) => ({ value: String(level), label: minutes(level) }))} />
        {!WEEKLY_LEVELS.includes(form.weekly_minutes) && <p className="z-small z-muted">{L.capacityCurrent(minutes(form.weekly_minutes))}</p>}
        {capacity && <CapacityMeter label={L.capacityScheduled} used={capacity.used_minutes} total={capacity.weekly_minutes} />}
      </Section></Surface>
      <Surface className="z-setting-group"><Section title={L.agentTime} note={L.agentTimeNote}>
        <ViewSwitch label={L.agentTime} value={String(form.agent_weekly_minutes)} onValueChange={(next) => set("agent_weekly_minutes", Number(next))} options={AGENT_LEVELS.map((level) => ({ value: String(level), label: minutes(level) }))} />
        {!AGENT_LEVELS.includes(form.agent_weekly_minutes) && <p className="z-small z-muted">{L.agentTimeCurrent(minutes(form.agent_weekly_minutes))}</p>}
        {week?.agent_capacity && <CapacityMeter label={L.agentTimeUsed} used={week.agent_capacity.used_minutes} total={week.agent_capacity.weekly_minutes} />}
        <Field label={L.agentCost} help={L.agentCostHelp}>
          <input className="z-input" type="number" inputMode="decimal" min={0} max={10000} step="0.01" aria-label={L.agentCost} value={form.agent_weekly_usd ?? ""} onChange={(event) => set("agent_weekly_usd", event.target.value === "" ? null : Number(event.target.value))} />
        </Field>
        {week?.agent_capacity && <p className="z-small z-muted">{L.agentCostUsed(usd(week.agent_capacity.used_usd), week.agent_capacity.weekly_usd === null ? "–" : usd(week.agent_capacity.weekly_usd))}{week.agent_capacity.unknown_cost_runs > 0 ? ` ${L.agentCostUnknown(week.agent_capacity.unknown_cost_runs)}` : ""}</p>}
      </Section></Surface>
      <Surface className="z-setting-group"><Section title={L.prepareAhead} note={undefined}>
        <div className="z-check-row">
          <Switch checked={form.prepare_ahead} onChange={(enabled) => set("prepare_ahead", enabled)} label={L.prepareAheadLabel} />
          <span>{form.prepare_ahead ? L.prepareAheadOn : L.prepareAheadOff}</span>
        </div>
      </Section></Surface>
      <Surface className="z-setting-group"><Section title={L.routing} note={L.routingNote}>
        <div className="z-check-row">
          <Switch checked={form.routing_judgments} onChange={(enabled) => set("routing_judgments", enabled)} label={L.routingLabel} />
          <span>{form.routing_judgments ? L.routingOn : L.routingOff}</span>
        </div>
      </Section></Surface>
      </div>
      <div className="z-toolbar"><span className="z-small z-muted">{D.saveNote}</span>{changed && <Pill tone="amber">{D.unsaved}</Pill>}<Button tone="primary" type="submit" busy={save.isPending} disabled={!changed}>{L.save}</Button></div>
      <ErrorText error={save.error} />
    </form>
    <Surface tone="red" className="z-setting-group"><Section title={D.danger}>
      <p className="z-muted">{L.deleteNote(product.name)}</p>
      <div><Button tone="danger" onClick={() => setDeleting(true)}>{L.delete(product.name)}</Button></div>
    </Section></Surface>
    <Modal open={deleting} onOpenChange={setDeleting} title={L.deleteTitle(product.name)} footer={<>
      <Button onClick={() => setDeleting(false)}>{L.cancel}</Button>
      <Button tone="primary" busy={remove.isPending} onClick={() => remove.mutate()}>{L.deleteConfirm}</Button>
    </>}><p>{L.irreversible}</p><ErrorText error={remove.error} /></Modal>
  </>;
}

